import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Dialog } from 'primereact/dialog';
import { FileUpload, FileUploadHeaderTemplateOptions, ItemTemplateOptions } from 'primereact/fileupload';
import Helper, { formatDateTime } from '../../../../core/services/Helper';
import { Appl } from '../../../../core/Appl';
import { Button, EmptyState, Icon, Input, InputDropdown, InputType, View, ViewHeader } from '../../../../core/components/Index';
import { classNames } from 'primereact/utils';
import { File } from 'lucide-react';
import SecureFileUploadViewModel from './SecureFileUploadViewModel';

export const SecureFileUploadView: React.FC = observer(() => {
    const vm = Appl.Services.get<SecureFileUploadViewModel>('SecureFileUploadViewModel');
    vm.navigate = useNavigate();
    const params: any = useParams();
    const uploadRef = useRef<FileUpload>(null);
    useAsyncEffect(async () => {
        await vm.initAsync(params.reqId, params.key);
    }, []);

    const onTemplateRemove = (file: File, callback: Function) => {
        callback();
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        return (
            <div className={`${options.className} mb-2`}>
                {options.chooseButton}
            </div>
        );
    };

    const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {
        const file = inFile as File;
        vm.activeDocument.docFilename = file.name;
        vm.activeDocument.file = file;
        vm.activeDocument.docFileSize = props.formatSize;
        return (
            <div className="w-full grid grid-flow-row place-items-center gap-2 py-4 max-w-2xl">
                <p className='text-xl tracking-1_5percent text-black font-medium'>You have selected the following document:</p>
                <div className='flex items-center pb-1'>
                    <span className='text-primary-500'><File size={18} className='mr-1' /></span>
                    <span className="text-black text-15px tracking-1_5percent break-all">{vm.activeDocument.docFilename} ({vm.activeDocument.docFileSize})</span>
                </div>
                <div className="flex items-center space-x-2">
                    <Button severity="warning" rounded label="Remove File" onClick={() => onTemplateRemove(file, props.onRemove)} />
                </div>
            </div >
        );
    };

    const progressBarTemplate = () => {
        return (
            <></>
        );
    };
    const emptyTemplate = () => {
        return (
            <div className='border-dashed rounded-lg border-[3px] border-[#B3B3B3] p-6'>
                <div className="text-center text-gray-800 font-medium mt-2 mb-2">Choose a file or drag & drop it here</div>
                <div className="text-center text-gray-400 mt-2 mb-2">{Appl.Setting.AllowedFileExtensions} formats, up to {Helper.formatBytes(Appl.Setting.AllowedFileSizeBytes)}</div>
                <div className="text-center text-gray-400 mt-2 mb-2">
                    <Button severity='warning' rounded outlined title='Browse Files' onClick={() => { uploadRef.current?.getInput().click() }} />
                </div>
            </div>
        );
    };

    return (
        <>
            <View id="UploadDocView" pageTitle={vm.pageTitle} showMenuBar={false}>
                {!vm.documentUploadLinkValid ? null : <ViewHeader title={vm.pageTitle} description={vm.pageDescription} />}
                <>
                    {vm.documentUploadLinkValid &&
                        <>
                            <div className="space-y-6">
                                <h1 className="font-semibold text-22px my-3">
                                    Welcome, {vm.requestModel.reqName}
                                </h1>
                            </div>
                            <div className="flex items-start flex-wrap md:flex-nowrap justify-between md:gap-x-8 pt-2 tracking--1_5percent text-15px">
                                <div className='w-full md:w-2/5 text-gray-900 leading-6'>
                                    <p>Thank you for reaching out to us. Based on your recent inquiry, we've identified some documents that may be necessary to assist with your request. To facilitate this process, we've provided this secure portal where you can easily upload the requested files. We've outlined the documents that were discussed during our communication, but we understand that there may be additional files that you feel are important to ensure your needs are fully met. You're welcome to upload any other relevant documents that you believe would assist in processing your request.</p>
                                    <p className='font-semibold mb-3 mt-6'>Please Note:</p>
                                    <p>
                                        <ul className='list-disc ml-4'>
                                            <li>This link will be valid for {vm.requestModel.expiryHours} hours and remain valid until:<br /> <b>{formatDateTime(vm.requestModel.targetDate)}</b>.</li>
                                            <li className='mt-4'>If you are unable to upload the requested documents within this timeframe, please contact us at (916) 874-9119 for further assistance.</li>
                                        </ul>
                                    </p>
                                </div>
                                {(vm.documentUploadSuccess && vm.requestModel.documents?.filter(o => o.docDtlId).length! > 0) &&

                                    <div className="md:w-3/5 pb-6 pt-1 w-full px-1">
                                        <div className="border border-primary-500 p-6">
                                            <div className='m-3 text-22px font-semibold text-primary-500 text-center'>You have uploaded{' '}{vm.requestModel.documents?.length}{' '}document{vm.requestModel.documents?.length
                                                === 1 ? '' : 's'
                                            }.</div>
                                            <div className='m-3 font-normal max-w-lg py-2 mx-auto'>
                                                <ul className='list-disc ml-4 text-left text-black tracking--1percent text-15px font-normal break-all'>
                                                    {vm.requestModel.documents?.map((doc, index) => (
                                                        <li key={index}>
                                                            {doc.docName} - <span className='italic'>{doc.docFilename} {doc.docFileSize ? `(${doc.docFileSize})` : ''}</span>
                                                        </li >
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className='m-3 font-bold text-center tracking--1percent text-15px'>You may now close this window at any time.</div>
                                        </div>
                                        <div className="border border-secondary-500 mt-6">
                                            <div className='mt-8 font-normal text-gray-600 text-center  tracking--1_5percent'>If you need to upload additional documents, use the Add Document button below.</div>
                                            <div className="flex items-center justify-center gap-x-4 my-8">
                                                <Button severity='warning' rounded outlined onClick={async () => { await vm.showUploadDialogAsync(true) }} title='Add Document' />
                                                <Button disabled={true} rounded title='Upload Document(s)' />
                                            </div>
                                        </div>
                                    </div>
                                }
                                {!vm.documentUploadSuccess &&
                                    <>
                                        <div className="border pb-4 pt-8 align-top md:w-3/5 w-full">
                                            <h1 className="font-semibold text-22px mb-3 text-center">Document(s) Added to Upload</h1>
                                            <div className='min-h-[250px]'>
                                                {!(vm.requestModel.documents && vm.requestModel.documents.length > 0) &&

                                                    <EmptyState className='max-w-sm mx-auto py-4' icon='file-plus' title="No Documents Added" description="Use the Add Document button below to select the file(s) you wish to upload.">
                                                        <div className='my-12 flex items-center justify-center gap-x-4'>
                                                            <Button severity='warning' rounded outlined onClick={async () => await vm.showUploadDialogAsync(false)} title='Add Document' />
                                                            <Button disabled={true} rounded onClick={async () => await vm.onSubmitAsync()} title='Upload Document(s)' />
                                                        </div>
                                                    </EmptyState>
                                                }

                                                {vm.requestModel.documents?.map((doc, index) => (
                                                    <div key={index} className="w-full grid grid-flow-row place-items-start gap-2 px-4 lg:px-8 py-3 pointer-events-none hover:bg-gray-50 max-w-2xl">
                                                        <div className='flex w-full'>
                                                            <div className='text-primary-500 mr-2 mt-0.5' title='Remove'>
                                                                <svg className='mt-0.5 pointer-events-auto cursor-pointer hover:text-red-500 w-4 h-4 text-neutral-400' fill="currentColor" onClick={() => Appl.ConfirmBox.show('This will permanently remove the selected file from your upload queue. If you wish to upload it later, you will need to select and classify the file again. <br/><br/>Are you sure you want to remove this file?', 'Remove Document?', '', 'Yes, Remove', 'Cancel', () => vm.onRemoveDocumentAsync(doc))} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                                                    <path d="M163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3C140.6 6.8 151.7 0 163.8 0zM32 128l384 0L394.8 467c-1.6 25.3-22.6 45-47.9 45l-245.8 0c-25.3 0-46.3-19.7-47.9-45L32 128zM143 239c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z" /></svg>
                                                            </div>
                                                            <div className='flex-1'>
                                                                <p className="font-semibold">{doc.docType}</p>
                                                                <p className="font-normal break-all">{doc.docName}</p>
                                                                <p className="font-normal break-all">{doc.docFilename} {doc.docFileSize ? `(${doc.docFileSize})` : ''}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {vm.requestModel.documents && vm.requestModel.documents.length > 0 &&
                                                <div className="flex items-center justify-center gap-x-4 my-8">
                                                    <Button severity='warning' rounded outlined onClick={async () => await vm.showUploadDialogAsync(false)} title='Add Document' />
                                                    <Button rounded onClick={async () => await vm.onSubmitAsync()} title='Upload Document(s)' />
                                                </div>
                                            }
                                        </div>
                                    </>
                                }

                            </div>
                        </>
                    }
                    {!vm.documentUploadLinkValid &&
                        <div className=' flex justify-center mx-auto max-w-[800px] w-full  '>
                            <div className="grid grid-flow-row place-items-center bg-gray-100 rounded-xl p-6 mt-8 w-full mx-auto"  >
                                <div className='m-3'>
                                    <svg height="79" viewBox="0 0 99 79" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-20 md:w-24'>
                                        <circle cx="32.5" cy="32.5" r="28.5" fill="white" stroke="#557121" strokeWidth="8" />
                                        <path d="M33 16L33 34" stroke="#557121" strokeWidth="5" stroke-linecap="round" />
                                        <path d="M33 34L40.0949 44.567" stroke="#557121" strokeWidth="5" stroke-linecap="round" />
                                        <rect x="58.4229" y="35.1799" width="16.8644" height="39.3504" fill="black" />
                                        <path d="M35 74C35 74.8625 35.225 75.7125 35.6625 76.4875C36.55 78.0375 38.2125 79 40 79H94C95.7875 79 97.45 78.0375 98.3375 76.4875C98.775 75.7125 99 74.8625 99 74C99 73.125 98.775 72.25 98.3125 71.475L71.3125 25.475C70.4125 23.9375 68.775 23 67 23C65.225 23 63.5875 23.9375 62.6875 25.475L35.6875 71.475C35.225 72.25 35 73.125 35 74ZM71 67C71 68.0609 70.5786 69.0783 69.8284 69.8284C69.0783 70.5786 68.0609 71 67 71C65.9391 71 64.9217 70.5786 64.1716 69.8284C63.4214 69.0783 63 68.0609 63 67C63 65.9391 63.4214 64.9217 64.1716 64.1716C64.9217 63.4214 65.9391 63 67 63C68.0609 63 69.0783 63.4214 69.8284 64.1716C70.5786 64.9217 71 65.9391 71 67ZM64 42C64 40.3375 65.3375 39 67 39C68.6625 39 70 40.3375 70 42V56C70 57.6625 68.6625 59 67 59C65.3375 59 64 57.6625 64 56V42Z" fill="#FFC107" />
                                    </svg>
                                </div>
                                <div className='m-3 text-4xl font-medium tracking--1_5percent'>{vm.documentUploadLinkInValidMessage == "expired" ? "Oops! This link has expired" : "Your upload request is already completed!"}</div>
                                {vm.documentUploadLinkInValidMessage == "expired" && <div className='m-3'>
                                    <div className='p-3'>It looks like the secure link you tried to use has expired. No worries, this can happen!</div>
                                    <div className='p-3'>To keep your information secure, our links are only active for 48 hours or until they're clicked. But don't stress—we've got you covered.</div>
                                    <div className='p-3 mb-0 pb-0'><b>What You Can Do:</b></div>
                                    <div className='p-3 ml-8'>
                                        <ul className='list-disc space-y-1'>
                                            <li><b>Get a New Link: </b> Call us at (916) 874-9119, and we'll send you a fresh link right away.</li>
                                            <li><b>Have Questions?</b> If you need any assistance, just give us a call. We're here for you!</li>
                                        </ul>
                                    </div>
                                    <div className='p-3'>Thanks for your patience—we'll get you sorted out in no time!</div>
                                </div>
                                }
                            </div>
                        </div>
                    }
                    {vm.uploadDialogVisible &&
                        <Dialog visible={vm.uploadDialogVisible} modal
                            blockScroll={true}
                            draggable={false}
                            header="Add Document"
                            onHide={async () => await vm.hideUploadDialogAsync()}>
                            <hr />
                            <div className='p-2 space-y-4'>
                                <div className='grid grid-cols-2 gap-4 mb-2'>
                                    <div>
                                        <InputDropdown
                                            id="docTypeId"
                                            value={vm.activeDocument?.docTypeId}
                                            options={vm.documentListModel}
                                            optionValue="key"
                                            optionLabel="value"
                                            label="Select Document Type"
                                            placeholder='Select Document Type'
                                            style={{ width: '375px' }}
                                            className="w-full"
                                            required={true}
                                            onChange={(e) => vm.onInputChange(e, 'docTypeId', vm.activeDocument)}
                                        />
                                    </div>
                                </div>
                                <FileUpload
                                    ref={uploadRef}
                                    mode="advanced"
                                    name={`files${vm.activeDocument?.docTypeId}`}
                                    accept={Appl.Setting.AllowedFileExtensions}
                                    maxFileSize={Appl.Setting.AllowedFileSizeBytes}
                                    headerTemplate={headerTemplate}
                                    itemTemplate={itemTemplate}
                                    emptyTemplate={emptyTemplate}
                                    progressBarTemplate={progressBarTemplate}
                                    contentClassName='border-none'
                                    headerClassName='hidden'
                                    chooseLabel="Browse Files"
                                    pt={{
                                        file: {
                                            className: classNames('flex items-center flex-wrap', 'p-4 border border-gray-300 dark:border-blue-900/40 rounded-[26px] gap-2 mb-2', 'last:mb-0')
                                        },
                                    }}
                                />
                                <div>
                                    <Input
                                        id="docName"
                                        value={vm.activeDocument.docName}
                                        inputType={InputType.Text}
                                        onChange={(e) => vm.onInputChange(e, 'docName', vm.activeDocument)}
                                        label="Name of Document"
                                        required={true}
                                        style={{ width: '375px' }}
                                        maxLength={100}
                                    />
                                </div>
                                <div className='flex justify-center'>
                                    <Button className="mt-4 me-2" rounded label="Add File" onClick={async () => await vm.onAddDocumentAsync()} />
                                    <Button severity='secondary' rounded className="mt-4" label="Cancel" onClick={async () => await vm.hideUploadDialogAsync()} />
                                </div>
                            </div>
                        </Dialog>
                    }
                </>
            </View >
        </>
    );
});
